/* combat actions (jump / burst / blast / slash / plating) + the world step — everything that mutates a live run */
import axios from "axios";
import { awardFeat, awardPoints, getLinkedTag } from "../../utils/rewards";
import { API, BEAST_ART, BOSS_PRESSURE, BOSS_WEAPON_MULT, BOSS_WORM_HP, BOSS_WORMS, DELIVERY_MAX, DELIVERY_TARGET, DEVOURED_BASELINE, GRAVITY, GROUND, isBossWave, isWormBossRound, JUMP_V, laserReach, loadArsenal, MELEE_WEAPONS, nextLockedWeapon, pushServerArsenal, RANGED_WEAPONS, saveArsenal, shotCooldown, shotPower, shotScale, W, weaponName, WEAPON_CRATE_SECONDS, weaponLevel, WORM_TIERS } from "./constants";

export function createCombat({ stateRef, phaseRef, pausedRef, playSfx }) {
    const jump = () => {
      if (phaseRef.current !== "running" || pausedRef.current) return;
      const g = stateRef.current;
      if (g.jumps < 2) {
        g.vy = JUMP_V * (g.jumps === 1 ? 0.88 : 1);
        g.jumps += 1;
        g.jumpTotal += 1;
        // every 20 jumps charges a speed cell — hold up to three
        if (g.jumpTotal % 20 === 0 && g.boosts < 3) {
          g.boosts += 1;
          g.boostFlash = 100;
          playSfx("coin");
        }
        playSfx("jump");
      }
    };

    // speed burst: burn a charged cell — distance racks up ~2x and the worm loses ground
    const fireBoost = () => {
      if (phaseRef.current !== "running" || pausedRef.current) return;
      const g = stateRef.current;
      if (g.boosts <= 0 || g.boost > 0) return;
      g.boosts -= 1;
      g.boost = 210;
      playSfx("jump");
      playSfx("coin");
    };

    // worm plating: hits from blade or boombox shatter it and drive the worm off.
    // upgraded boombox rounds hit harder each delivery — later rounds drop plating in one shot
    const hitWorm = (push, damage = 1) => {
      const g = stateRef.current;
      g.wormHp -= damage;
      // boss waves: enraged plating shrugs off knockback — hits push it back far less
      g.worm = Math.min(230, g.worm + push * (isBossWave(g) ? 0.6 : 1));
      for (let i = 0; i < 4; i += 1) {
        g.dust.push({ x: 150 - g.worm + 56, y: GROUND - 70 + g.rng() * 50, vx: 1 + g.rng() * 3, life: 16 });
      }
      playSfx("roar");
      if (g.wormHp <= 0) {
        g.wormHp = isBossWave(g) ? 8 : 5; // enraged Apex plating is thicker
        g.repels += 1;
        g.repelFlash = 120;
        g.worm = 230;
        if (isBossWave(g)) {
          // boss kill bounty: shattering the enraged Apex plating pays real PTS
          g.bossShatters += 1;
          g.bountyFlash = 150;
          g.credits += 25; // 625-point bounty
          awardPoints("wasteland", 15);
          playSfx("coin");
        } else {
          g.credits += 10; // 250-point bounty
        }
        playSfx("roar");
      }
    };

    // ranged slot: boombox blaster, machine gun boombox, or grenade cassettes.
    // smart-aims backward at a closing worm, else clears the lane ahead.
    // every 10,000 PTS the payload levels up — missiles, larger fireballs, faster cycling, heavier hits
    const fireShot = () => {
      if (phaseRef.current !== "running" || pausedRef.current) return;
      const g = stateRef.current;
      if (g.shotCd > 0) return;
      // boss duel: the named worm stands ahead — all fire runs forward at BOSS LEVEL damage
      const bossUp = Boolean(g.wormBoss);
      const bossMult = bossUp ? BOSS_WEAPON_MULT : 1;
      const dir = bossUp ? 1 : g.worm < 150 ? -1 : 1;
      g.shootDir = dir;
      g.shootAnim = 16;
      if (g.ranged === "machinegun") {
        // rapid-fire chassis — lighter slugs at nearly double the cycle speed
        g.shotCd = Math.max(5, shotCooldown(g) - 5);
        g.shots.push({ x: 198 + dir * 46, y: g.y + 78, vx: dir * 17, r: Math.max(0.7, shotScale(g) * 0.7), pow: shotPower(g) * bossMult, missile: weaponLevel(g) >= 3, bossUp });
        playSfx("blast");
        return;
      }
      if (g.ranged === "grenades") {
        // grenade cassettes — slow arc, splash blast on impact
        g.shotCd = 30;
        g.shots.push({ type: "grenade", x: 198 + dir * 30, y: g.y + 60, vx: dir * 6.5, vy: -6.5, r: 1, pow: Math.max(3, shotPower(g)) * bossMult, bossUp });
        playSfx("blast");
        return;
      }
      g.shotCd = shotCooldown(g);
      g.shots.push({ x: 198 + dir * 46, y: g.y + 78, vx: dir * 13, r: shotScale(g), pow: shotPower(g) * bossMult, missile: weaponLevel(g) >= 3, bossUp });
      playSfx("blast");
    };

    // mono-filament slash: shreds debris in arm's reach, bites a worm that's close
    const doSlash = () => {
      if (phaseRef.current !== "running" || pausedRef.current) return;
      const g = stateRef.current;
      if (g.slashCd > 0) return;
      const laser = g.melee === "laser";
      g.slashCd = laser ? 30 : 26;
      g.slash = 14;
      g.laserSlash = laser;
      if (laser) g.laserSwings = (g.laserSwings || 0) + 1; // the blade stretches with use
      const reach = laser ? laserReach(g) : 0; // laser sword cuts a much wider arc that grows
      playSfx("slash");
      g.obstacles.forEach((o) => {
        if (!o.hit && o.x > 168 && o.x < 348 + reach) {
          o.hit = true;
          o.shredded = true;
        }
      });
      // the blade also cuts crows out of the air
      g.crows.forEach((c) => {
        if (!c.dead && c.x > 150 && c.x < 360 + reach && Math.abs(c.y - (g.y + 46)) < 92) {
          c.dead = true;
          g.credits += c.ember ? 6 : 3;
          g.crowKills += 1;
          for (let i = 0; i < 4; i += 1) {
            g.dust.push({ x: c.x, y: c.y, vx: (g.rng() - 0.5) * 4, life: 18 });
          }
          if (g.crowKills === 5) awardFeat("CROW HUNTER");
        }
      });
      // and drops charging beasts at ground level — wolves fold, bears stagger
      g.beasts.forEach((b) => {
        if (b.dead || b.x < 150 || b.x > 360 + reach) return;
        b.hp -= 1;
        if (b.hp <= 0) {
          b.dead = true;
          g.credits += BEAST_ART[b.kind].credits;
          g.beastKills += 1;
          for (let i = 0; i < 6; i += 1) {
            g.dust.push({ x: b.x, y: GROUND - 30, vx: (g.rng() - 0.5) * 5, life: 20 });
          }
          if (g.beastKills === 8) awardFeat("BEASTBANE");
        } else {
          b.hitFlash = 8;
          b.x += 16; // the brute staggers back a stride
        }
      });
      if (g.worm < 100 + (laser ? laserReach(g) - 65 : 0)) hitWorm(12, laser ? 2 : 1);
    };

  return { jump, fireBoost, hitWorm, fireShot, doSlash };
}

/* one simulation tick — the caller guards phase/pause; env carries the React-side effects */
export function stepWorld(g, { playSfx, hitWorm, setScore, setBest, phaseRef, setPhase, setActiveBoost, setDevoured, clearSuspend, onArsenalChange }) {
        g.runFrames += 1; // live-run clock — one tick ≈ 1/60s of actual running
        // near-death slow-mo heartbeat: time halves when the worm is at Dolo's heels
        if (g.slowmo > 0) g.slowmo -= 1;
        if (g.slowmoCooldown > 0) g.slowmoCooldown -= 1;
        if (!g.slowmoArmed && g.worm > 150) g.slowmoArmed = true;
        if (g.slowmoArmed && g.slowmoCooldown <= 0 && g.worm < 60 && g.worm > 25) {
          g.slowmo = 110;
          g.slowmoArmed = false;
          g.slowmoCooldown = 600;
          playSfx("heartbeat");
          playSfx("roar"); // plays pitch-bent — the trigger already flipped slow-mo on
        }
        const advance = g.slowmo <= 0 || g.frame % 2 === 0;
        if (advance) {
        const tubeSpeed = g.tubeBoost === "speed" ? 1.35 : 1;
        g.speed = Math.min(12.5 * tubeSpeed, (5.8 + g.frame * 0.0013) * tubeSpeed);
        // boss duel: the world holds its breath — no route progress while the named worm stands
        if (!g.wormBoss) g.distance += g.speed * (g.boost > 0 ? 0.114 : 0.06);
        // threat escalation: surviving long enough wakes a worse worm
        for (let i = WORM_TIERS.length - 1; i > g.tier; i -= 1) {
          if (g.distance >= WORM_TIERS[i].at) {
            g.tier = i;
            g.tierFlash = 130;
            g.worm = Math.min(230, g.worm + 14); // brief relief while it molts
            playSfx("roar");
            break;
          }
        }
        // the worm relentlessly closes in; each class is hungrier than the last.
        // boss waves: the Apex Devourer hunts enraged until the delivery lands.
        // boss duels: the chaser hangs back and circles while the named worm fights
        if (g.wormBoss) {
          g.worm = Math.min(190, g.worm + 0.5);
        } else {
          g.worm = Math.min(230, g.worm - (isBossWave(g) ? BOSS_PRESSURE : WORM_TIERS[g.tier].pressure));
        }
        if (g.invuln > 0) g.invuln -= 1;
        if (g.tierFlash > 0) g.tierFlash -= 1;
        // night shift: dusk settles in as the worm tiers up
        const nightTarget = [0, 0.22, 0.42][g.tier] ?? 0.42;
        g.night += (nightTarget - g.night) * 0.02;
        if (g.boostFlash > 0) g.boostFlash -= 1;
        // burst drive: the worm loses ground while it burns
        if (g.boost > 0) {
          g.boost -= 1;
          g.worm = Math.min(230, g.worm + 0.5);
        }

        // ---------- boss worm duel: entrance, slam telegraphs, shockwaves, hull damage ----------
        if (g.wormBoss) {
          const wb = g.wormBoss;
          if (wb.flash > 0) wb.flash -= 1;
          // pose state machine — roar while burrowing in, attack on the slam, damage on hits, idle otherwise
          if (wb.poseT > 0) wb.poseT -= 1;
          else wb.pose = wb.x > wb.targetX ? "roar" : "idle";
          if (wb.x > wb.targetX) {
            wb.x -= 6;
            if (g.frame % 4 === 0) g.dust.push({ x: wb.x - 60 + g.rng() * 160, y: GROUND - g.rng() * 30, vx: -1 - g.rng() * 2, life: 20 });
          } else {
            wb.slamCd -= 1;
            if (wb.slamCd <= 0) {
              // ground slam — a rolling shockwave Dolo must jump
              wb.slamCd = 220 + g.rng() * 130;
              wb.flash = 12;
              wb.pose = "attack";
              wb.poseT = 55;
              g.bossShocks.push({ x: wb.x - 200, life: 400 });
              playSfx(BOSS_WORMS[wb.variant].sfx.slam); // each worm slams in its own register
            }
            // weak point windows — a glowing soft spot phases onto the boss's face for ~2.5s;
            // shots landing at its height pay DOUBLE hull damage. skilled timing: most windows
            // sit above hip height, so jump-and-fire is the play
            if (wb.weakWin > 0) {
              wb.weakWin -= 1;
            } else {
              wb.weakCd -= 1;
              if (wb.weakCd <= 0) {
                wb.weakWin = 150;
                wb.weakCd = 300 + g.rng() * 140;
                wb.weakY = 55 + g.rng() * 175;
                playSfx("heartbeat");
              }
            }
          }
          g.bossShocks.forEach((s) => { s.x -= 7.5; s.life -= 1; });
          // shockwave contact — only a real jump clears it; getting caught slams Dolo toward the chaser
          if (g.invuln <= 0) {
            for (const s of g.bossShocks) {
              if (!s.got && Math.abs(s.x - 198) < 34 && g.y > GROUND - 160) {
                s.got = true;
                g.worm -= 60;
                g.invuln = 70;
                playSfx("hurt");
                for (let i = 0; i < 6; i += 1) {
                  g.dust.push({ x: 198, y: GROUND - 30, vx: (g.rng() - 0.5) * 5, life: 20 });
                }
                break;
              }
            }
          }
        g.bossShocks = g.bossShocks.filter((s) => !s.got && s.x > -60 && s.life > 0);
        }
        // weak-crit floaters drift up and fade — even after the boss bursts
        g.crits.forEach((c) => { c.y -= 0.7; c.life -= 1; });
        g.crits = g.crits.filter((c) => c.life > 0);
        // boss death beat — the carcass settles, then clears
        if (g.bossDying) {
          g.bossDying.t -= 1;
          if (g.bossDying.t <= 0) g.bossDying = null;
        }

        // boombox rounds and cassette splash tear into the boss hull at BOSS LEVEL damage
        const damageBoss = (amount, x, y) => {
          const wb = g.wormBoss;
          if (!wb) return;
          // weak point window — a shot landing at the glow's height crits for double
          const weakHit = wb.weakWin > 0 && Math.abs(y - (GROUND - wb.weakY)) < 38;
          wb.hp -= weakHit ? amount * 2 : amount;
          wb.flash = weakHit ? 10 : 6;
          wb.pose = "damage";
          wb.poseT = 16;
          for (let i = 0; i < 5; i += 1) {
            g.dust.push({ x, y: Math.min(y, GROUND - 10), vx: -1 - g.rng() * 3, life: 16 });
          }
          if (weakHit) {
            g.crits.push({ x, y: y - 14, txt: `WEAK ×2 — ${(amount * 2).toLocaleString()}`, life: 46 });
            if (!g.weakCritLanded) {
              g.weakCritLanded = true;
              awardFeat("DEADEYE");
            }
            playSfx("coin");
          } else {
            playSfx("blast");
          }
          if (wb.hp > 0) return;
          // hull broken — the boss worm bursts apart in a chain of explosions
          const name = BOSS_WORMS[wb.variant].name;
          for (let i = 0; i < 7; i += 1) {
            g.booms.push({ x: wb.x - 200 + g.rng() * 220, y: GROUND - 30 - g.rng() * 200, life: 22 });
          }
          // trophy wall tally — local instantly, the route ledger carries it to the tag on report
          g.bossVariantKills[wb.variant] += 1;
          try {
            const tally = JSON.parse(localStorage.getItem("wasteland-boss-trophies") || "[0,0,0,0,0]");
            if (Array.isArray(tally) && tally.length === 5) {
              tally[wb.variant] = (Number(tally[wb.variant]) || 0) + 1;
              localStorage.setItem("wasteland-boss-trophies", JSON.stringify(tally));
              window.dispatchEvent(new Event("wasteland-trophies-changed"));
            }
          } catch { /* local tally is best-effort */ }
          g.bossDying = { variant: wb.variant, x: wb.x, t: 55 }; // death pose plays out as the body drops
          g.wormBoss = null;
          g.bossShocks = [];
          g.wormBossKills += 1;
          g.bossKillFlash = 190;
          g.bossEngageFlash = 0;
          g.bossKillName = name;
          g.credits += 40; // 1,000-point boss bounty
          g.invuln = Math.max(g.invuln, 90);
          g.worm = Math.min(230, g.worm + 70); // the chaser reels while the dust settles
          awardPoints("wasteland", 20);
          if (g.wormBossKills === 1) awardFeat("WORMBREAKER");
          playSfx(BOSS_WORMS[wb.variant].sfx.roar); // death wail in the boss's own voice
          playSfx("coin");
        };

        // combat timers
        if (g.shotCd > 0) g.shotCd -= 1;
        if (g.slashCd > 0) g.slashCd -= 1;
        if (g.slash > 0) g.slash -= 1;
        if (g.shootAnim > 0) g.shootAnim -= 1;
        if (g.repelFlash > 0) g.repelFlash -= 1;
        if (g.deliveryFlash > 0) g.deliveryFlash -= 1;
        if (g.bossFlash > 0) g.bossFlash -= 1;
        if (g.bossEngageFlash > 0) g.bossEngageFlash -= 1;
        if (g.bossKillFlash > 0) g.bossKillFlash -= 1;
        if (g.bountyFlash > 0) g.bountyFlash -= 1;
        if (g.weaponFlash > 0) g.weaponFlash -= 1;
        if (g.weaponBoost > 0) g.weaponBoost -= 1;
        if (g.crateFlash > 0) g.crateFlash -= 1;
        if (g.dropFlash > 0) g.dropFlash -= 1;
        g.booms.forEach((b) => { b.life -= 1; });
        g.booms = g.booms.filter((b) => b.life > 0);

        // boombox fire: forward shots shred debris, backward shots bite the worm.
        // larger upgraded fireballs get a wider hit window; grenade cassettes arc and splash
        g.shots.forEach((s) => {
          if (s.type === "grenade") {
            s.vy += 0.35;
            s.x += s.vx;
            s.y += s.vy;
            let boom = s.y >= GROUND - 8;
            if (!boom) {
              for (const o of g.obstacles) {
                if (!o.hit && s.x > o.x - 10 && s.x < o.x + o.w + 10 && s.y > GROUND - o.h - 10) { boom = true; break; }
              }
            }
            if (!boom) {
              for (const c of g.crows) {
                if (!c.dead && Math.abs(s.x - c.x) < 34 && Math.abs(s.y - c.y) < 40) { boom = true; break; }
              }
            }
            if (!boom) {
              for (const b of g.beasts) {
                if (!b.dead && Math.abs(s.x - b.x) < 46 && s.y > GROUND - 120) { boom = true; break; }
              }
            }
            if (!boom && g.wormBoss && s.x >= g.wormBoss.x - 200 && s.y > GROUND - 280) boom = true;
            if (!boom && s.vx < 0 && s.x <= 150 - g.worm + 70) boom = true;
            if (boom) {
              s.got = true;
              g.booms.push({ x: s.x, y: Math.min(s.y, GROUND - 8), life: 22 });
              playSfx("slash");
              // splash: shred debris, drop crows, bite the worm
              g.obstacles.forEach((o) => {
                if (!o.hit && Math.abs(o.x + o.w / 2 - s.x) < 110) {
                  o.hit = true;
                  o.shredded = true;
                }
              });
              g.crows.forEach((c) => {
                if (!c.dead && Math.abs(c.x - s.x) < 120 && Math.abs(c.y - s.y) < 120) {
                  c.dead = true;
                  g.credits += c.ember ? 6 : 3;
                  g.crowKills += 1;
                  if (g.crowKills === 5) awardFeat("CROW HUNTER");
                }
              });
              // splash staggers the pack too — wolves drop, bears take double
              g.beasts.forEach((b) => {
                if (b.dead || Math.abs(b.x - s.x) >= 120) return;
                b.hp -= 2;
                if (b.hp <= 0) {
                  b.dead = true;
                  g.credits += BEAST_ART[b.kind].credits;
                  g.beastKills += 1;
                  for (let i = 0; i < 6; i += 1) {
                    g.dust.push({ x: b.x, y: GROUND - 30, vx: (g.rng() - 0.5) * 5, life: 20 });
                  }
                  if (g.beastKills === 8) awardFeat("BEASTBANE");
                } else {
                  b.hitFlash = 8;
                  b.x += 16;
                }
              });
              if (s.x <= 150 - g.worm + 110) hitWorm(4, s.pow);
              if (g.wormBoss && s.x > g.wormBoss.x - 260) damageBoss(s.pow, s.x, Math.min(s.y, GROUND - 30));
              return;
            }
            return;
          }
          const sr = s.r || 1;
          s.x += s.vx;
          if (s.vx > 0) {
            if (g.wormBoss && s.x >= g.wormBoss.x - 200 && s.y > GROUND - 280) {
              s.got = true;
              damageBoss(s.pow || 1, s.x, s.y);
              return;
            }
            for (const o of g.obstacles) {
              if (!o.hit && s.x > o.x - 4 * sr && s.x < o.x + o.w + 4 * sr && s.y + 14 * sr > GROUND - o.h) {
                o.hit = true;
                o.shredded = true;
                s.got = true;
                playSfx("blast");
                break;
              }
            }
          } else if (s.x <= 150 - g.worm + 60) {
            s.got = true;
            hitWorm(7, s.pow || 1);
          }
        });
        g.shots = g.shots.filter((s) => !s.got && s.x > -60 && s.x < W + 60);

        // dolo physics
        g.vy += GRAVITY;
        g.y += g.vy;
        if (g.y >= GROUND - 118) {
          g.y = GROUND - 118;
          g.vy = 0;
          g.jumps = 0;
        }

        // spawn obstacles + coin arcs — the field stays clear during a boss duel
        if (!g.wormBoss && g.frame > g.nextObstacle) {
          const tall = g.rng() > 0.55;
          const oh = tall ? 66 + g.rng() * 34 : 28 + g.rng() * 26;
          const ow = 26 + g.rng() * 34;
          g.obstacles.push({ x: W + 40, w: ow, h: oh });
          if (g.rng() > 0.35) {
            for (let i = 0; i < 3; i += 1) {
              g.coins.push({ x: W + 40 + i * 34, y: GROUND - oh - 60 - Math.sin((i / 2) * Math.PI) * 34 });
            }
          }
          // rare golden cell rides above the arc — a lucky free burst charge
          if (g.rng() < 0.12) {
            g.cells.push({ x: W + 74, y: GROUND - oh - 96 });
          }
          g.nextObstacle = g.frame + (70 + g.rng() * 70) * (6 / g.speed);
        }
        g.obstacles.forEach((o) => { o.x -= g.speed; });
        g.coins.forEach((c) => { c.x -= g.speed; });
        g.cells.forEach((c) => { c.x -= g.speed; });
        g.weaponCrates.forEach((c) => { c.x -= g.speed; });

        // rare weapon crate drop — a supply drop carrying a temporary MK overdrive
        if (!g.wormBoss && g.distance >= g.nextCrate) {
          g.weaponCrates.push({ x: W + 40, got: false });
          g.nextCrate = g.distance + 700 + g.rng() * 900;
        }

        // rare crimson weapon case — carries the next unsecured arsenal weapon
        const lockedWeapon = nextLockedWeapon(g.owned);
        if (lockedWeapon && !g.wormBoss && g.distance >= g.nextDrop) {
          g.weaponDrops.push({ x: W + 40, weapon: lockedWeapon, got: false });
          g.nextDrop = g.distance + 1300 + g.rng() * 900;
        }
        g.weaponDrops.forEach((c) => { c.x -= g.speed; });
        g.weaponDrops = g.weaponDrops.filter((c) => !c.got && c.x > -40);
        // shredded debris bursts apart, then leaves the world
        g.obstacles.forEach((o) => {
          if (o.shredded && !o.burst) {
            o.burst = true;
            for (let i = 0; i < 5; i += 1) {
              g.dust.push({ x: o.x + o.w / 2, y: GROUND - o.h / 2, vx: -g.speed * 0.4 + (g.rng() - 0.5) * 3, life: 18 });
            }
          }
        });
        g.obstacles = g.obstacles.filter((o) => !o.shredded && o.x + o.w > -60);
        g.coins = g.coins.filter((c) => !c.got && c.x > -40);
        g.cells = g.cells.filter((c) => !c.got && c.x > -40);
        g.weaponCrates = g.weaponCrates.filter((c) => !c.got && c.x > -40);

        // running dust
        if (g.y >= GROUND - 118 && g.frame % 5 === 0) {
          g.dust.push({ x: 150, y: GROUND - 6, vx: -g.speed * 0.5, life: 22 });
        }
        g.dust.forEach((d) => { d.x += d.vx; d.life -= 1; });
        g.dust = g.dust.filter((d) => d.life > 0);

        // dolo hitbox (forgiving)
        const hx = 150 + 26;
        const hw = 97 - 52;
        const hy = g.y + 14;
        const hh = 118 - 26;

        // coin pickup
        g.coins.forEach((c) => {
          if (!c.got && Math.abs(c.x - (hx + hw / 2)) < 44 && Math.abs(c.y - (hy + hh / 2)) < 64) {
            c.got = true;
            g.credits += 1;
            playSfx("coin");
          }
        });

        // golden cell pickup — free burst charge (or +5 CR scrap when the bank is full)
        g.cells.forEach((c) => {
          if (!c.got && Math.abs(c.x - (hx + hw / 2)) < 50 && Math.abs(c.y - (hy + hh / 2)) < 68) {
            c.got = true;
            if (g.boosts < 3) {
              g.boosts += 1;
              g.boostFlash = 100;
            } else {
              g.credits += 5;
            }
            playSfx("coin");
            playSfx("jump");
          }
        });

        // weapon crate pickup — temporary MK overdrive beyond the current level
        g.weaponCrates.forEach((c) => {
          if (!c.got && Math.abs(c.x - (hx + hw / 2)) < 46 && Math.abs(GROUND - 16 - (hy + hh / 2)) < 70) {
            c.got = true;
            g.weaponBoost = WEAPON_CRATE_SECONDS * 60;
            g.weaponLevel = weaponLevel(g); // absorb the overdrive jump — the crate banner announces it
            g.crateFlash = 150;
            playSfx("coin");
            playSfx("blast");
          }
        });

        // crimson weapon case pickup — permanently secures the weapon into the arsenal
        g.weaponDrops.forEach((c) => {
          if (!c.got && Math.abs(c.x - (hx + hw / 2)) < 46 && Math.abs(GROUND - 16 - (hy + hh / 2)) < 70) {
            c.got = true;
            if (!g.owned.includes(c.weapon)) g.owned.push(c.weapon);
            saveArsenal({ ...loadArsenal(), owned: g.owned });
            pushServerArsenal(getLinkedTag(), { owned: g.owned, ranged: g.ranged, melee: g.melee });
            g.dropFlash = 170;
            g.dropName = weaponName(c.weapon);
            playSfx("coin");
            playSfx("blast");
            playSfx("roar");
            if (onArsenalChange) onArsenalChange();
          }
        });

        // obstacle collision
        for (const o of g.obstacles) {
          if (hx < o.x + o.w - 6 && hx + hw > o.x + 6 && hy + hh > GROUND - o.h + 6) {
            if (g.invuln <= 0) {
              if (g.tubeShield > 0) {
                // Bio Hack shield eats the hit — no worm damage
                g.tubeShield -= 1;
                g.invuln = 110;
                o.hit = true;
              } else {
                g.worm -= 82;
                g.invuln = 70;
                o.hit = true;
              }
            }
          }
        }

        // ---------- mutated crows — sky hunters that dive on Dolo ----------
        if (g.skyFlash > 0) g.skyFlash -= 1;
        const killCrow = (c, via) => {
          if (c.dead) return;
          c.dead = true;
          g.credits += c.ember ? 6 : 3;
          g.crowKills += 1;
          for (let i = 0; i < 4; i += 1) {
            g.dust.push({ x: c.x, y: c.y, vx: (g.rng() - 0.5) * 4, life: 18 });
          }
          playSfx(via === "slash" ? "slash" : "blast");
          if (g.crowKills === 5) awardFeat("CROW HUNTER");
        };
        if (!g.wormBoss && g.frame > g.nextCrow && g.crows.length < 3) {
          // night stirs the ember flock — faster, hungrier, worth double
          const ember = g.tier >= 2 && g.rng() < 0.45;
          g.crows.push({
            x: W + 90,
            y: 80 + g.rng() * (GROUND - 340),
            bob: g.rng() * Math.PI * 2,
            tilt: 0,
            mode: "approach",
            ember,
            dead: false,
          });
          g.nextCrow = g.frame + 380 + g.rng() * 340;
        }
        const doloCy = g.y + 46;
        g.crows.forEach((c) => {
          if (c.dead) return;
          const chase = (Math.max(2.4, g.speed * 0.5) + 1.8) * (c.ember ? 1.4 : 1);
          if (c.mode === "approach") {
            // level flight in from the eastern sky, wings pumping
            c.x -= chase;
            c.y += Math.sin(g.frame * 0.12 + c.bob) * 1.7;
            c.tilt = Math.sin(g.frame * 0.12 + c.bob) * 0.08;
            if (c.x < 540) {
              c.mode = "dive";
              // swoop warning — screech and a red tear across the sky so the slash can be timed
              if (!c.warned) {
                c.warned = true;
                c.diveAt = g.frame;
                g.skyFlash = 16;
                playSfx("screech");
              }
            }
          } else if (c.mode === "dive") {
            // homes on Dolo's chest — capped turn rate keeps the swoop readable
            c.x -= chase * 1.05;
            const dy = doloCy - c.y;
            const cap = c.ember ? 6.4 : 4.4;
            const step = Math.max(-cap, Math.min(cap, dy * 0.05));
            c.y += step;
            c.tilt = Math.max(-0.4, Math.min(0.4, step * 0.09));
            if (c.x < 30) c.mode = "exit";
          } else {
            // overshot — climbs back into the smog
            c.x -= chase * 1.15;
            c.y -= 3.1;
            c.tilt = -0.3;
          }
          // boombox rounds tear it out of the sky — upgraded fireballs have a wider blast radius
          g.shots.forEach((s) => {
            const sr = s.r || 1;
            if (!s.got && Math.abs(s.x - c.x) < 42 * sr && Math.abs(s.y - c.y) < 46 * sr) {
              s.got = true;
              killCrow(c, "blast");
            }
          });
          // beak-first contact with Dolo
          if (!c.dead && g.invuln <= 0 && Math.abs(c.x - (hx + hw / 2)) < 44 && Math.abs(c.y - (hy + hh / 2)) < 58) {
            c.dead = true;
            for (let i = 0; i < 4; i += 1) {
              g.dust.push({ x: c.x, y: c.y, vx: (g.rng() - 0.5) * 4, life: 18 });
            }
            if (g.tubeShield > 0) {
              g.tubeShield -= 1;
              g.credits += c.ember ? 6 : 3;
              g.crowKills += 1;
              playSfx("blast");
              if (g.crowKills === 5) awardFeat("CROW HUNTER");
            } else {
              g.worm -= 60;
              g.invuln = 70;
              playSfx("hurt");
            }
          }
        });
        g.crows = g.crows.filter((c) => !c.dead && c.x > -140 && c.y > -160);

        // ---------- mutated chargers — wolf packs dart in fast, horned bears bully through ----------
        if (g.beastFlash > 0) g.beastFlash -= 1;
        const fellBeast = (b, via) => {
          if (b.dead) return;
          b.dead = true;
          g.credits += BEAST_ART[b.kind].credits;
          g.beastKills += 1;
          for (let i = 0; i < 6; i += 1) {
            g.dust.push({ x: b.x, y: GROUND - 30, vx: (g.rng() - 0.5) * 5, life: 20 });
          }
          playSfx(via === "slash" ? "slash" : "blast");
          if (g.beastKills === 8) awardFeat("BEASTBANE");
        };
        const hitBeast = (b, dmg, via) => {
          if (b.dead) return;
          b.hp -= dmg;
          if (b.hp <= 0) {
            fellBeast(b, via);
            return;
          }
          b.hitFlash = 8;
          b.x += 16; // staggered back a stride by the impact
          playSfx("blast");
        };
        // the horned bear joins the hunt on every third spawn — the pack sits out boss waves
        if (!g.wormBoss && g.frame > g.nextBeast && g.beasts.length < 2 && !isBossWave(g)) {
          g.beastSpawns += 1;
          const kind = g.beastSpawns % 3 === 2 ? "bear" : "wolf";
          g.beasts.push({ kind, x: W + 120, hp: BEAST_ART[kind].hp, hitFlash: 0, warned: false, dead: false, bob: g.rng() * 40 });
          g.nextBeast = g.frame + 380 + g.rng() * 300;
        }
        g.beasts.forEach((b) => {
          if (b.dead) return;
          const art = BEAST_ART[b.kind];
          // ground charge — faster than the world scroll so it genuinely gains on Dolo
          b.x -= g.speed + art.speed;
          if (b.hitFlash > 0) b.hitFlash -= 1;
          // charge warning — the road tears red so the jump or slash can be timed
          if (!b.warned && b.x < 620) {
            b.warned = true;
            g.beastFlash = 14;
            playSfx("screech");
          }
          // boombox rounds — wolves drop to one, bears soak plating hits
          g.shots.forEach((s) => {
            const sr = s.r || 1;
            if (!s.got && s.vx > 0 && Math.abs(s.x - b.x) < (art.w / 2 + 10) * sr && s.y > GROUND - 120) {
              s.got = true;
              hitBeast(b, 1, "blast");
            }
          });
          // head-on contact — the beast slams Dolo backward toward the worm
          const doloLow = g.y > GROUND - 150; // a real jump clears the charge
          if (!b.dead && g.invuln <= 0 && doloLow && Math.abs(b.x - (hx + hw / 2)) < art.w / 2 + 26) {
            if (g.tubeShield > 0) {
              g.tubeShield -= 1;
              fellBeast(b, "blast");
            } else {
              b.dead = true; // it barrels through and vanishes into the dust
              g.worm -= art.wormHit;
              g.invuln = 70;
              playSfx("hurt");
              for (let i = 0; i < 6; i += 1) {
                g.dust.push({ x: b.x, y: GROUND - 30, vx: (g.rng() - 0.5) * 5, life: 20 });
              }
            }
          }
        });
        g.beasts = g.beasts.filter((b) => !b.dead && b.x > -160);

        // delivery-route ledger: report the finished run (route clear or worm catch) for the Hall of Fame
        const reportDeliveryRun = (total, cleared) => {
          const deliveries = Math.min(DELIVERY_MAX, g.delivery - 1);
          if (!cleared && deliveries <= 0 && g.bossShatters <= 0 && !g.bossVariantKills.some(Boolean)) return; // nothing logged — don't spam the ledger
          axios
            .post(`${API}/wasteland/delivery`, {
              tag: getLinkedTag(),
              deliveries,
              cleared,
              elapsed_seconds: Math.max(1, Math.round(g.runFrames / 60)),
              score: total,
              boss_shatters: g.bossShatters,
              boss_worm_kills: g.bossVariantKills,
            })
            .catch(() => {});
        };

        // ---------- delivery route: every 30,000 PTS completes a delivery ----------
        const total = Math.floor(g.distance) + g.credits * 25;

        // ---------- boombox weapon upgrade: every 10,000 PTS ----------
        const wLevel = weaponLevel(g);
        if (wLevel > g.weaponLevel) {
          g.weaponLevel = wLevel;
          g.weaponFlash = 130;
          playSfx("blast");
        }

        if (!g.wormBoss && g.delivery <= DELIVERY_MAX && total >= g.delivery * DELIVERY_TARGET) {
          g.delivery += 1;
          if (g.delivery > DELIVERY_MAX) {
            // route cleared — all 20 deliveries logged, the game is beaten
            setScore(total);
            setBest((prev) => {
              const next = Math.max(prev, total);
              localStorage.setItem("wasteland-run-best", String(next));
              return next;
            });
            phaseRef.current = "complete";
            setPhase("complete");
            setActiveBoost(null);
            awardPoints("wasteland", 100);
            awardFeat("ROUTE MASTER");
            playSfx("coin");
            playSfx("roar");
            clearSuspend();
            reportDeliveryRun(total, true);
            return;
          }
          // delivery logged — boombox upgrades (missiles, larger fireballs) and the worm staggers back
          if (isWormBossRound(g)) {
            // every other round complete — a named boss worm erupts ahead for a duel.
            // supersedes the Apex boss wave when the schedules cross (10th/20th)
            const variant = g.bossFights % BOSS_WORMS.length;
            g.bossFights += 1;
            g.wormBoss = { variant, hp: BOSS_WORM_HP, max: BOSS_WORM_HP, x: W + 220, targetX: W - 250, slamCd: 200, flash: 0, weakCd: 260, weakWin: 0, weakY: 120, pose: "roar", poseT: 0 };
            g.bossEngageFlash = 240;
            g.bossKillFlash = 0;
            g.deliveryFlash = 0;
            g.bossFlash = 0;
            g.invuln = Math.max(g.invuln, 100);
            g.obstacles = [];
            g.crows = [];
            g.beasts = [];
            awardPoints("wasteland", 5);
            playSfx("coin");
            playSfx(BOSS_WORMS[variant].sfx.roar); // the named worm announces itself in its own voice
            playSfx("heartbeat");
          } else if (isBossWave(g)) {
            // every 5th delivery opens a boss wave — the Apex Devourer hunts enraged until it lands
            g.bossFlash = 220;
            g.deliveryFlash = 0;
            g.worm = Math.min(230, g.worm + 40); // a shorter breather — the Apex is already hunting
            g.invuln = Math.max(g.invuln, 100);
            awardPoints("wasteland", 5);
            playSfx("coin");
            playSfx("roar");
            playSfx("heartbeat");
          } else {
            g.deliveryFlash = 170;
            g.worm = Math.min(230, g.worm + 70);
            g.invuln = Math.max(g.invuln, 100);
            awardPoints("wasteland", 5);
            playSfx("coin");
            playSfx("roar");
          }
        }

        if (g.worm <= 25) {
          setScore(total);
          setBest((prev) => {
            const next = Math.max(prev, total);
            localStorage.setItem("wasteland-run-best", String(next));
            return next;
          });
          phaseRef.current = "over";
          setPhase("over");
          setActiveBoost(null);
          awardPoints("wasteland", Math.min(25, 2 + Math.floor(total / 150)));
          if (total >= 1000) awardFeat("KILOMETER CLUB");
          playSfx("roar");
          clearSuspend();
          reportDeliveryRun(total, false);
          // one more runner feeds the worm — report to the site-wide tally (crow kills ride along for the Hall of Fame)
          axios
            .post(`${API}/worm/kill`, { tier: WORM_TIERS[g.tier].name, score: total, crow_kills: g.crowKills, tag: getLinkedTag() })
            .then(({ data }) => {
              if (typeof data.kills === "number") setDevoured(DEVOURED_BASELINE + data.kills);
            })
            .catch(() => {});
        }
        }
}
